import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, UserSquare, Search, Trash2 } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Avatar from '@/components/ui/Avatar';
import Input from '@/components/ui/Input';
import DataTable from '@/components/ui/DataTable';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import StudentForm from './StudentForm';
import StudentDetail from './StudentDetail';
import { GOAL_LABELS } from '@/lib/enums';
import styles from './Students.module.css';

const PLAN_KEYS = ['free', 'mensal', 'trimestral', 'anual', 'pro'];

function FreqBars({ value = 0, total = 5 }) {
  return (
    <span className={styles.freq}>
      {Array.from({ length: total }).map((_, i) => (
        <span key={i} className={i < value ? styles.freqOn : styles.freqOff} />
      ))}
    </span>
  );
}

export default function TrainerStudents() {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [invite, setInvite] = useState(null); // { name, url } pós-criação
  const [copied, setCopied] = useState(false);
  const [q, setQ] = useState('');

  const load = () => {
    setLoading(true);
    api
      .get('/students')
      .then((res) => setStudents((res.data || res).students || res.data || []))
      .catch(() => setStudents([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const create = async (payload) => {
    const res = await api.post('/students', payload);
    const data = res.data || res;
    setCreating(false);
    // Conta criada sem senha → mostra o link de convite pro personal enviar.
    if (data.inviteUrl) {
      setCopied(false);
      setInvite({ name: data.student?.name || payload.name, url: data.inviteUrl });
    }
    load();
  };

  const remove = async (e, s) => {
    e.stopPropagation();
    if (!confirm(t('students.confirmRemove', { name: s.name }))) return;
    try {
      await api.delete(`/students/${s.id}`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return students;
    return students.filter((s) => `${s.name} ${s.email}`.toLowerCase().includes(term));
  }, [students, q]);

  const columns = [
    {
      key: 'name',
      header: t('students.columns.student'),
      render: (s) => (
        <div className={styles.aluno}>
          <Avatar name={s.name} src={s.avatar} size={36} />
          <div>
            <div className={styles.alunoName}>{s.name}</div>
            {s.email && <div className={styles.alunoEmail}>{s.email}</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'goal',
      header: t('students.columns.goal'),
      render: (s) => {
        const goal = s.goal || s.studentProfile?.goal;
        return <span className={styles.muted}>{goal ? GOAL_LABELS[goal] || goal : '—'}</span>;
      },
    },
    {
      key: 'plan',
      header: t('students.columns.plan'),
      render: (s) => <span className={styles.hi}>{PLAN_KEYS.includes(s.plan) ? t(`students.plans.${s.plan}`) : (s.plan || '—')}</span>,
    },
    {
      key: 'freq',
      header: t('students.columns.frequency'),
      render: (s) => <FreqBars value={Number(s.weeklyFrequency || s.studentProfile?.weeklyFrequency || 0)} />,
    },
    {
      key: 'status',
      header: t('screens.status'),
      align: 'center',
      width: 70,
      render: (s) => {
        const active = (s.plan && s.plan !== 'free');
        return <span className={styles.dot} style={{ background: active ? 'var(--volt)' : 'var(--mid)' }} />;
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 44,
      render: (s) => (
        <button className={styles.del} onClick={(e) => remove(e, s)} aria-label={t('common.delete')}>
          <Trash2 size={15} strokeWidth={2} />
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader kicker="Movez · Personal" title={`${t('nav.students')}${students.length ? ` · ${students.length}` : ''}`}>
        <Button icon={UserPlus} onClick={() => setCreating(true)}>{t('screens.addStudent')}</Button>
      </PageHeader>

      <div className={styles.searchRow}>
        <span className={styles.searchIcon}><Search size={16} strokeWidth={2} /></span>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('students.searchPlaceholder')}
          style={{ flex: 1 }}
        />
      </div>

      <Card padding="none">
        <DataTable
          columns={columns}
          rows={filtered}
          loading={loading}
          onRowClick={(s) => setDetail(s)}
          empty={
            <EmptyState
              icon={UserSquare}
              title={t('students.empty.title')}
              description={t('students.empty.description')}
              action={<Button icon={UserPlus} onClick={() => setCreating(true)}>{t('screens.addStudent')}</Button>}
            />
          }
        />
      </Card>

      <Modal open={creating} onClose={() => setCreating(false)} kicker={t('students.modalNewKicker')} title={t('students.modalNewTitle')} size="md">
        <StudentForm onSubmit={create} onCancel={() => setCreating(false)} />
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} kicker={t('students.modalDetailKicker')} title={detail?.name || ''} size="lg">
        {detail && <StudentDetail student={detail} />}
      </Modal>

      <Modal open={!!invite} onClose={() => setInvite(null)} kicker="Movez" title={t('students.invite.title')} size="md">
        {invite && (
          <div style={{ display: 'grid', gap: 14 }}>
            <p style={{ color: 'var(--mid)', fontSize: 14, lineHeight: 1.5 }}>
              {t('students.invite.desc', { name: invite.name })}
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Input value={invite.url} readOnly style={{ flex: 1 }} onFocus={(e) => e.target.select()} />
              <Button
                onClick={() => {
                  navigator.clipboard?.writeText(invite.url);
                  setCopied(true);
                }}
              >
                {copied ? t('students.invite.copied') : t('students.invite.copy')}
              </Button>
            </div>
            <p style={{ color: 'var(--low)', fontSize: 12 }}>{t('students.invite.emailNote')}</p>
          </div>
        )}
      </Modal>
    </>
  );
}
